import { useRef, useState } from 'react';
import {
    AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader,
    AlertDialogOverlay, Button, Text, useToast,
} from '@chakra-ui/react';
import { authAPI } from '../../services/api';

// 收回訊息的確認框（老師 PWA 對話室 MobileChatRoom 與桌面對話頁 TeacherChatPage 共用）。
//
// 【為什麼把「確認 + 打 API + 錯誤提示」收在同一個元件】
// 兩個對話室的資料流是刻意逐項對齊的，這段若各寫一份，錯誤處理與文案遲早漂移
// （這一整條聊天功能的共用元件都是為了避免「同一件事維護三份」才拆出來的）。
//
// 【為什麼由頁面掛一個，而不是每顆氣泡自己掛】
// 一串對話動輒上百顆氣泡，每顆各掛一個 AlertDialog 等於多上百個 portal 節點。
// 氣泡只負責回報「使用者要收回這一則」（onRecall），對話框由頁面層持有。
//
// message ＝ null 時整個框是關閉狀態（Chakra 的 isOpen 控制），所以呼叫端只要用一個
// `recallTarget` state 就夠，不必再配一個布林。
export default function RecallConfirmDialog({ message, onClose, onRecalled }) {
    const cancelRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    const handleConfirm = async () => {
        if (!message) return;
        setLoading(true);
        try {
            const r = await authAPI.chat.recallMessage(message.id);
            // 後端回傳的是「已清空內容」的那一則（is_recalled=true），直接拿它替換本地那筆。
            // 不等 WebSocket 才更新：WS 是盡力而為的管道（斷線時根本不會來），自己按下去的
            // 動作不該依賴它才有反應。WS 事件晚一步到也無妨——同一個 id、同樣的內容覆蓋上去。
            onRecalled?.(r.data);
            onClose?.();
        } catch (err) {
            // 400 的 detail 是寫給使用者看的整句話（例如「超過 24 小時的訊息無法收回」），
            // 直接顯示；用一句籠統的「操作失敗」蓋掉會讓老師以為是系統壞了而一直重試。
            toast({
                title: '收回失敗',
                description: err.response?.data?.detail || '請稍後再試',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <AlertDialog
            isOpen={!!message}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
            isCentered
        >
            <AlertDialogOverlay>
                {/* AlertDialog 自帶白底，內文照常用 neutral 階（畫布上的 onCanvas 階不適用於此） */}
                <AlertDialogContent mx={4}>
                    <AlertDialogHeader fontSize="md" fontWeight="bold" color="neutral.800">
                        收回這則訊息？
                    </AlertDialogHeader>
                    <AlertDialogBody>
                        <Text fontSize="sm" color="neutral.600">
                            雙方畫面都會變成「訊息已收回」，內容不再顯示。
                        </Text>
                        {/* 收回不等於對方沒看過——先講清楚，老師才不會以為按下去就沒事了 */}
                        <Text fontSize="xs" color="neutral.500" mt={2}>
                            但已經看過的人仍然記得內容，必要時請另外向家長說明。
                        </Text>
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelRef} size="sm" onClick={onClose} isDisabled={loading}>
                            取消
                        </Button>
                        <Button colorScheme="red" size="sm" ml={3} onClick={handleConfirm} isLoading={loading}>
                            收回
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
}
